import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Box,
    Typography,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material'

// Boolean variable to enable adding replacement TBD
const ENABLE_REPLACEMENT_TBD = true

const getOptionId = (value) => {
    if (value == null || value === '') return null
    if (typeof value === 'object') {
        return value.id ?? value.user_id ?? null
    }
    return value
}

const isSameOption = (optionId, selectedId) => String(optionId) === String(selectedId)

const AddEditTBDDialog = ({ open, onClose, onSave, tbd, tbds, dropdownOptions }) => {
    const isEditing = !!tbd
    const showReplacementTbdType = ENABLE_REPLACEMENT_TBD

    const [formData, setFormData] = useState({
        tbd_id: '',
        tbd_type: 'growth',
        user_id: null,
        designation: null,
        department: null,
        sub_department: null,
        vacancy_type: 'permanent',
        number_of_positions: 1,
        reporting_to_user_id: null,
    })

    const [errors, setErrors] = useState({})

    // Function to calculate next TBD ID
    const generateNextTbdId = () => {
        if (!tbds || tbds.length === 0) return 'TBD1001'

        const tbdNumbers = tbds
            .map((tbd) => {
                const match = tbd.tbd_id?.match(/^TBD(\d+)$/)
                return match ? parseInt(match[1]) : 0
            })
            .filter((num) => num > 0)

        const maxNumber = tbdNumbers.length > 0 ? Math.max(...tbdNumbers) : 1000
        return `TBD${maxNumber + 1}`
    }

    useEffect(() => {
        if (tbd) {
            setFormData({
                tbd_id: tbd.tbd_id || '',
                tbd_type: tbd.tbd_type || 'growth',
                user_id: getOptionId(tbd.user_id ?? tbd.user),
                designation: getOptionId(tbd.designation ?? tbd.designation_id),
                department: getOptionId(tbd.department ?? tbd.department_list_data_id),
                sub_department: getOptionId(
                    tbd.sub_department ?? tbd.subfunction ?? tbd.subfunction_list_data_id
                ),
                vacancy_type: tbd.vacancy_type || 'permanent',
                number_of_positions: tbd.number_of_positions || 1,
                reporting_to_user_id: getOptionId(
                    tbd.reporting_to_user_id ?? tbd.reporting_to_user
                ),
            })
        } else {
            // For new TBD, prefill with next available ID
            setFormData({
                tbd_id: generateNextTbdId(),
                tbd_type: 'growth',
                user_id: null,
                designation: null,
                department: null,
                sub_department: null,
                vacancy_type: 'permanent',
                number_of_positions: 1,
                reporting_to_user_id: null,
            })
        }
        setErrors({})
    }, [tbd, open, tbds])

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }))
        }
    }

    const validate = () => {
        const newErrors = {}

        // TBD ID is auto-generated on backend if not provided, so not required here

        if (!formData.tbd_type) {
            newErrors.tbd_type = 'TBD Type is required'
        }

        if (!formData.number_of_positions || formData.number_of_positions < 1) {
            newErrors.number_of_positions = 'Number of positions must be at least 1'
        }

        if (
            !formData.designation &&
            !formData.department &&
            !formData.sub_department &&
            formData.tbd_type !== 'replacement'
        ) {
            newErrors.general = 'Please select at least one of: Designation, Department, or Sub-function'
        }

        if (formData.tbd_type === 'replacement' && !formData.user_id) {
            newErrors.user_id = 'User to be replaced is required'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = () => {
        if (!validate()) return

        // Remove empty tbd_id to trigger auto-generation on backend
        const dataToSave = { ...formData }
        if (!dataToSave.tbd_id || !dataToSave.tbd_id.trim()) {
            delete dataToSave.tbd_id
        }

        onSave(dataToSave)
    }

    const selectedDesignation = dropdownOptions.designations?.find(
        (d) => isSameOption(d.id, formData.designation)
    )
    const selectedDepartment = dropdownOptions.departments?.find(
        (d) => isSameOption(d.id, formData.department)
    )
    const selectedSubfunction = dropdownOptions.subfunctions?.find(
        (d) => isSameOption(d.id, formData.sub_department)
    )
    const selectedReportingUser = dropdownOptions.users?.find(
        (u) => isSameOption(u.id, formData.reporting_to_user_id)
    )
    const selectedUserToBeReplaced = dropdownOptions.users?.find(
        (u) => isSameOption(u.id, formData.user_id)
    )

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{isEditing ? 'Edit TBD' : 'Add New TBD'}</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    {errors.general && (
                        <Typography variant="body2" color="error">
                            {errors.general}
                        </Typography>
                    )}

                    {/* TBD ID */}
                    <TextField
                        label="TBD ID"
                        value={formData.tbd_id}
                        disabled
                        fullWidth
                        helperText={isEditing ? "TBD ID cannot be changed" : "Auto-generated TBD ID"}
                    />

                    {/* TBD Type */}
                    <FormControl fullWidth>
                        <InputLabel>TBD Type</InputLabel>
                        <Select
                            value={formData.tbd_type}
                            onChange={(e) => handleChange('tbd_type', e.target.value)}
                            label="TBD Type"
                            disabled={isEditing || !showReplacementTbdType}
                        >
                            <MenuItem value="growth">Growth</MenuItem>
                            <MenuItem value="replacement">Replacement</MenuItem>
                        </Select>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, ml: 1.5 }}>
                            {isEditing ? 'Cannot be changed after creation' : 'Set at creation'}
                        </Typography>
                    </FormControl>

                    {/* User ID - User being replaced */}
                    {formData.tbd_type === 'replacement' && (
                        <Autocomplete
                            options={dropdownOptions.users || []}
                            getOptionLabel={(option) => `${option.name} (${option.email})` || ''}
                            value={selectedUserToBeReplaced || null}
                            onChange={(_, value) => handleChange('user_id', value?.id || null)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="User to be Replaced"
                                    error={!!errors.user_id}
                                    helperText={errors.user_id || (isEditing ? "Cannot be changed after creation" : "")}
                                    required
                                />
                            )}
                            disabled={isEditing}
                            fullWidth
                        />
                    )}

                    {/* Designation */}
                    <Autocomplete
                        options={dropdownOptions.designations || []}
                        getOptionLabel={(option) => option.value || ''}
                        value={selectedDesignation || null}
                        onChange={(_, value) => handleChange('designation', value?.id || null)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Designation"
                                error={!!errors.designation}
                                helperText={errors.designation}
                            />
                        )}
                        fullWidth
                    />

                    {/* Department */}
                    <Autocomplete
                        options={dropdownOptions.departments || []}
                        getOptionLabel={(option) => option.value || ''}
                        value={selectedDepartment || null}
                        onChange={(_, value) => handleChange('department', value?.id || null)}
                        renderInput={(params) => <TextField {...params} label="Department" />}
                        fullWidth
                    />

                    {/* Sub-function */}
                    <Autocomplete
                        options={dropdownOptions.subfunctions || []}
                        getOptionLabel={(option) => option.value || ''}
                        value={selectedSubfunction || null}
                        onChange={(_, value) => handleChange('sub_department', value?.id || null)}
                        renderInput={(params) => <TextField {...params} label="Sub-function" />}
                        fullWidth
                    />

                    {/* Vacancy Type */}
                    <FormControl fullWidth>
                        <InputLabel>Vacancy Type</InputLabel>
                        <Select
                            value={formData.vacancy_type}
                            onChange={(e) => handleChange('vacancy_type', e.target.value)}
                            label="Vacancy Type"
                        >
                            <MenuItem value="permanent">Permanent</MenuItem>
                            <MenuItem value="temporary">Temporary</MenuItem>
                            <MenuItem value="contract">Contract</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Number of Positions */}
                    <TextField
                        label="Number of Positions"
                        type="number"
                        value={formData.number_of_positions}
                        onChange={(e) => handleChange('number_of_positions', parseInt(e.target.value) || 1)}
                        error={!!errors.number_of_positions}
                        helperText={errors.number_of_positions}
                        InputProps={{ inputProps: { min: 1 } }}
                        required
                        fullWidth
                    />

                    {/* Reporting To */}
                    <Autocomplete
                        options={dropdownOptions.users || []}
                        getOptionLabel={(option) => `${option.name} (${option.email})` || ''}
                        value={selectedReportingUser || null}
                        onChange={(_, value) => handleChange('reporting_to_user_id', value?.id || null)}
                        renderInput={(params) => <TextField {...params} label="Reporting To" />}
                        fullWidth
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" color="primary">
                    {isEditing ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default AddEditTBDDialog
